import React from 'react';
import { AlertTriangle, RefreshCw, ArrowLeft } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('RouteErrorBoundary caught an error:', error, errorInfo);
    
    // Handle ChunkLoadError specifically - usually a caching issue after deployment
    if (error && error.name === 'ChunkLoadError') {
      console.warn('ChunkLoadError detected - likely a stale cache. Attempting to reload...');
      // Force a hard reload to clear cache and get fresh chunks
      setTimeout(() => {
        // Clear cache and reload
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => caches.delete(name));
          });
        }
        window.location.reload();
      }, 1000);
      return;
    }
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log route-specific errors
    if (process.env.NODE_ENV === 'production') {
      this.logRouteError(error, errorInfo);
    }
  }

  logRouteError = (error, errorInfo) => {
    console.error('Route error logging:', {
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      route: this.props.location?.pathname || 'unknown',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    });
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      const { error } = this.state;
      const { routeName = 'this page' } = this.props;

      return (
        <div className="min-h-[50vh] flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
            </div>
            
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Error loading {routeName}
            </h2>
            
            <p className="text-gray-600 mb-6">
              {error && error.name === 'ChunkLoadError' 
                ? 'The app was recently updated. Please refresh the page to load the latest version.'
                : 'There was a problem loading this page. This might be a temporary issue.'}
            </p>
            
            {error && error.name === 'ChunkLoadError' && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Page will automatically refresh in a moment...
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </button>
              
              <button
                onClick={() => window.history.back()}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && error && (
              <div className="mt-4 p-3 bg-gray-100 rounded-lg text-left">
                <h3 className="font-medium text-gray-900 mb-2">Error Details:</h3>
                <pre className="text-xs text-gray-600 overflow-auto max-h-20">
                  {error.toString()}
                </pre>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component to wrap routes
export const withRouteErrorBoundary = (Component, routeName) => {
  return function WrappedComponent(props) {
    return (
      <RouteErrorBoundary routeName={routeName}>
        <Component {...props} />
      </RouteErrorBoundary>
    );
  };
};

export default RouteErrorBoundary;
